import { Box, SelectChangeEvent, Typography, styled } from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AsyncAutocomplete, Option } from '../../../components/common/AsyncAutocomplete';
import Button from '../../../components/common/Button';
import FileUploadField from '../../../components/common/FileUploadField';
import Loading from '../../../components/common/Loading';
import SelectField from '../../../components/common/SelectField';
import TextField from '../../../components/common/TextField';
import api from '../../../utils/api';

const Subtitle = styled(Typography)({
  fontSize: '13px',
  marginBottom: '24px',
  textAlign: 'left',
  fontWeight: 500,
  color: 'black',
});

const FormContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '32px',
  marginBottom: '24px',
  width: '80%',
});

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '32px',
  width: '80%',
});

interface AddApplicantProps {
  initialData?: {
    vacancyType: string;
    vacancyCode: string;
    vacancy: string;  
    source: string;
    sourceReferral: string;
    designation: string;
  };
}

const AddApplicant: React.FC<AddApplicantProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialData = location.state?.initialData;
  const [formData, setFormData] = useState({
    vacancyType: initialData?.vacancyType || '',
    vacancyCode: initialData?.vacancyCode || '',
    vacancy: initialData?.vacancy || '',
    source: initialData?.source || '',
    sourceReferral: initialData?.sourceReferral || '',
    designation: initialData?.designation || '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [vacancies, setVacancies] = useState<{ id: number; vacancyName: string, vacancyCode: string }[]>([]);

  useEffect(() => {
    const fetchVacanciesAndDesignations = async () => {
      try {
        setLoading(true);
        const response = await api.get<{ vacancies: { id: number; vacancyName: string, vacancyCode: string }[] }>('/rec/vacancy/list');

        setVacancies(response.data.vacancies);
        if (initialData) {
          const selectedVacancy = response.data.vacancies.find(v => v.vacancyName === initialData?.vacancy)
          setFormData(prev => ({
            ...prev,
            vacancy: selectedVacancy?.id.toString() || '',
            vacancyCode: selectedVacancy?.id.toString() || '',
          }));
        }
      } catch (error) {
        console.error('Error fetching vacancies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVacanciesAndDesignations();
  }, [initialData]);

  const handleLoadDesignations = async (query: string) => {
    const response = await api.get<{ data: { id: number; designation: string }[] }>(`/rec/vacancy/designations?query=${query}`);
    return response.data.data.map((item: any) => ({
      value: item.id,
      label: item.designation,
    }));
  };

  const handleTextChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleFileChange = (newFile: File | null) => {
    setFileError('');
    if (!newFile) {
      setFile(null);
      return;
    }

    const allowedTypes = ['.pdf', '.docx', '.doc'];
    const fileExtension = '.' + newFile.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      setFileError('Please upload a PDF, DOCX, or DOC file');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (newFile.size > maxSize) {
      setFileError('File size should be less than 5MB');
      return;
    }

    setFile(newFile);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    // Check required fields
    if (!formData.vacancyType) {
      errors.vacancyType = 'Vacancy Type is required';
      isValid = false;
    }
    if (!formData.vacancyCode) {
      errors.vacancyCode = 'Vacancy Code is required';
      isValid = false;
    }
    if (!formData.vacancy) {
      errors.vacancy = 'Vacancy is required';
      isValid = false;
    }
    if (!formData.source) {
      errors.source = 'Source is required';
      isValid = false;
    }
    if (!file) {
      setFileError('Please upload a CV file');
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setSubmitError('');

      const formDataToSend = new FormData();
      formDataToSend.append('vacancyType', formData.vacancyType);
      formDataToSend.append('vacancyCode', vacancies.find(v => v.id === parseInt(formData.vacancyCode))?.vacancyCode || '');
      formDataToSend.append('vacancy', vacancies.find(v => v.id === parseInt(formData.vacancy))?.vacancyName || '');
      formDataToSend.append('source', formData.source);
      formDataToSend.append('sourceReferral', formData.sourceReferral);
      formDataToSend.append('designation', formData.designation?.label || '');
      if (file) {
        formDataToSend.append('file', file, file.name);
      }

      if (initialData) {
        formDataToSend.append('linkedinId', initialData.id.toString());
      }

      const response = await api.post('/rec/applicant/add', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        navigate('/rec/applicant/search');
      }

    } catch (error: any) {
      console.error('Error adding applicant:', error);
      setSubmitError(error.response?.data?.message || 'Failed to add applicant. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Implement cancel functionality
    setFormData({
      vacancyType: '',
      vacancyCode: '',
      vacancy: '',
      source: '',
      sourceReferral: '',
      designation: '',
    });
    setFile(null);
    setFileError('');
  };

  return (
    <Box className='page-container'>
      <Typography className='page-title'>Add Applicant</Typography>
      <Box className='edit-section' marginTop="24px">
        <Subtitle>Please Input The Vacancy Information And Upload CVs For Available Positions</Subtitle>

        {submitError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {submitError}
          </Typography>
        )}

        {loading ?
          <Loading /> :
          <>
            <FormContainer>
              <SelectField
                label="Vacancy Type"
                required
                width="100%"
                value={formData.vacancyType}
                onChange={(event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
                  setFormData(prev => ({
                    ...prev,
                    vacancyType: event.target.value as string
                  }));
                  setFieldErrors(prev => ({ ...prev, vacancyType: '' }));
                }}
                error={!!fieldErrors.vacancyType}
                helperText={fieldErrors.vacancyType}
                options={[
                  { value: 'TAAS', label: 'TAAS' },
                  { value: 'EOR', label: 'EOR' },
                ]}
              />

              <SelectField
                label="Vacancy Code"
                required
                width="100%"
                value={formData.vacancyCode}
                onChange={(event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
                  setFormData(prev => ({
                    ...prev,
                    vacancyCode: event.target.value as string,
                    vacancy: event.target.value as string
                  }));
                  setFieldErrors(prev => ({ ...prev, vacancyCode: '' }));
                }}
                error={!!fieldErrors.vacancyCode}
                helperText={fieldErrors.vacancyCode}
                options={loading ? [{ value: '', label: 'Loading...' }] : vacancies.map((vacancy) => ({
                  value: vacancy.id.toString(),
                  label: vacancy.vacancyCode
                }))}
              />

              <SelectField
                label="Vacancy"
                required
                width="100%"
                value={formData.vacancy}
                onChange={(event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
                  setFormData(prev => ({
                    ...prev,
                    vacancy: event.target.value as string,
                    vacancyCode: event.target.value as string
                  }));
                  setFieldErrors(prev => ({ ...prev, vacancy: '' }));
                }}
                error={!!fieldErrors.vacancy}
                helperText={fieldErrors.vacancy}
                options={loading ? [{ value: '', label: 'Loading...' }] : vacancies.map((vacancy) => ({
                  value: vacancy.id.toString(),
                  label: vacancy.vacancyName
                }))}
              />

              <SelectField
                label="Source"
                required
                width="100%"
                value={formData.source}
                onChange={(event: SelectChangeEvent<unknown>, child: React.ReactNode) => {
                  setFormData(prev => ({
                    ...prev,
                    source: event.target.value as string
                  }));
                  setFieldErrors(prev => ({ ...prev, source: '' }));
                }}
                error={!!fieldErrors.source}
                helperText={fieldErrors.source}
                options={[
                  { value: 'direct', label: 'Direct' },
                  { value: 'linkedin', label: 'LinkedIn' },
                  { value: 'referral', label: 'Referral' },
                  { value: 'agency', label: 'Agency' },
                  { value: 'website', label: 'Website' },
                ]}
              />

              <TextField
                label="Source Referral"
                width="100%"
                value={formData.sourceReferral}
                onChange={handleTextChange('sourceReferral')}
              />

              <AsyncAutocomplete
                label="Designation"
                width="100%"
                value={formData.designation}
                onChange={(value: Option | null) => {
                  setFormData(prev => ({
                    ...prev,
                    designation: value
                  }));
                }}
                loadOptions={handleLoadDesignations}
              />

            </FormContainer>

            <FileUploadField
              label="Upload CV (Pdf, Docx Or Doc Format)"
              required
              onChange={handleFileChange}
              error={fileError}
              width="174px"
              value={file}
              accept='.png, .jpg, .jpeg'
            />
          </>}
      </Box>
      <ButtonContainer>
        <Button
          variant="outlined"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </ButtonContainer>
    </Box>
  );
};

export default AddApplicant; 